import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { API } from "@/lib/api";

const HERO_IMG = `${API}/files/yarnthread/hero/kichazone-all-purses.jpg`;

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease: [0.76, 0, 0.24, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden border-b border-line">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-24 pt-16 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-32 lg:pt-24">
                <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="mb-8 font-mono text-xs uppercase tracking-[0.25em] text-clay"
                        data-testid="hero-overline"
                    >
                        Handmade &amp; curated — Est. 2026
                    </motion.p>
                    <h1 className="font-serif text-5xl font-light leading-[0.95] tracking-tighter text-gold sm:text-6xl" data-testid="hero-heading">
                        <MaskedLine delay={0.15}>Kichazone</MaskedLine>
                        <MaskedLine delay={0.3} className="italic text-espresso">Where Sophistication</MaskedLine>
                        <MaskedLine delay={0.45} className="italic text-espresso">Meets Convenience.</MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65 }}
                        className="mt-6 font-serif text-xl italic text-gold md:text-2xl"
                        data-testid="hero-tagline"
                    >
                        Where Beauty Meets Style, Comfort &amp; Elegance.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="mt-6 max-w-md text-base leading-relaxed text-clay"
                        data-testid="hero-subcopy"
                    >
                        Discover a beautiful collection of handmade purses, handcrafted resin coasters, elegant jewelry boxes, decorative jewelry bowls, stylish accessories, and more — each created or carefully selected to bring beauty, elegance, and a touch of luxury into your everyday life.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso/70"
                        data-testid="hero-motto"
                    >
                        Carry Your Style. Elevate Your Space. Embrace Your Beauty.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/shop"
                            data-testid="hero-shop-button"
                            className="group flex items-center gap-3 bg-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso"
                        >
                            Shop the collection
                            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <button
                            onClick={() => document.getElementById("craft")?.scrollIntoView({ behavior: "smooth" })}
                            data-testid="hero-story-button"
                            className="border border-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-espresso hover:text-sand"
                        >
                            Our craft
                        </button>
                    </motion.div>
                </motion.div>
                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="relative ml-auto w-full max-w-md"
                    >
                        <div className="absolute -left-4 -top-4 h-full w-full border border-espresso" aria-hidden="true" />
                        <div className="relative">
                            <motion.img
                                src={HERO_IMG}
                                alt="The Kichazone handmade purse collection"
                                style={{ y: imgY }}
                                className="h-auto w-full"
                                data-testid="hero-image"
                            />
                        </div>
                        <div className="absolute -bottom-5 right-6 bg-gold px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso" data-testid="hero-badge">
                            01 / One-of-one
                        </div>
                    </motion.div>
                </div>
            </div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-6 left-6 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-clay lg:flex lg:left-10"
            >
                <ArrowDown size={12} className="animate-bounce" /> Scroll slowly
            </motion.div>
        </section>
    );
};
